import { useMemo } from "react";
import type {
    DetailPanelContent,
    ExplorerEntityType,
    ExplorerNode,
    IPropertySection,
} from "../../declarations/IExplorerTypes";
import type { IComparisonInfo } from "../../declarations/types";
import type { IBomLineCS } from "../../hooks/Route/useGetBomLinesCS";
import type { IProductD365 } from "../../hooks/Product/useGetProductD365";
import type { IReleasedProductD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductD365";
import type { ILineExistenceInfo } from "../../hooks/SalesOrderLine/useGetSalesOrderLineExistence";
import { splitPascalCase } from "../../utilities/ObjectUtilities";

interface ILineComparisonResult {
    data: IComparisonInfo[] | undefined;
    isLoading: boolean;
    error: Error | null;
}

interface IUseDetailPanelContentParams {
    bomLineMap?: Map<string, IBomLineCS[]>;
    bomRPComparisonMap?: Map<string, { data?: IComparisonInfo[]; isLoading: boolean }>;
    buyProductMap?: Map<string, { data?: IProductD365; isLoading: boolean; isError: boolean }>;
    buyRPMap?: Map<string, { data?: IReleasedProductD365; isLoading: boolean; isError: boolean }>;
    comparisonDataMap: Record<string, IComparisonInfo[] | undefined>;
    existenceData: ILineExistenceInfo[] | undefined;
    isLineDetailSelected: boolean;
    isLinesListSelected: boolean;
    lineComparison: ILineComparisonResult;
    nodes: ExplorerNode[];
    routeComparisonMap?: Map<string, { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[]; isLoading: boolean }>;
    selectedEntityType: ExplorerEntityType | null;
    selectedNodeId: string | null;
}

export function useDetailPanelContent({
    bomLineMap,
    bomRPComparisonMap,
    buyProductMap,
    buyRPMap,
    comparisonDataMap,
    existenceData,
    isLineDetailSelected,
    isLinesListSelected,
    lineComparison,
    nodes,
    routeComparisonMap,
    selectedEntityType,
    selectedNodeId,
}: IUseDetailPanelContentParams): DetailPanelContent {
    return useMemo(() => {
        if (!selectedNodeId || !selectedEntityType) {
            return { type: "empty" };
        }

        // Line detail — lazy loaded comparison
        if (isLineDetailSelected) {
            if (lineComparison.isLoading) {
                return { type: "loading" };
            }
            if (lineComparison.error) {
                return { type: "error", message: lineComparison.error.message };
            }
            if (lineComparison.data) {
                return { type: "comparison", data: lineComparison.data };
            }
            return { type: "empty" };
        }

        // Lines list — existence table
        if (isLinesListSelected && existenceData) {
            return { type: "lineExistence", data: existenceData };
        }

        const selectedNode = nodes.find((n) => n.id === selectedNodeId);
        const isD365Source = selectedNode?.data.source === "d365";

        // Production Order nodes — D365-sourced; one section per order, or a clear
        // "fulfilled from stock" empty state when no production order was raised.
        if (selectedNodeId.startsWith("ProductionOrder-")) {
            const node = nodes.find((n) => n.id === selectedNodeId);
            if (node?.data.status === "loading") {
                return { type: "loading" };
            }
            if (node?.data.status === "error") {
                return { type: "error", message: "Could not load production orders from D365." };
            }
            const orders = node?.data.productionOrders ?? [];
            if (orders.length === 0) {
                return {
                    type: "empty",
                    message: "Fulfilled from stock — no production order was raised for this sales order.",
                };
            }
            const sections: IPropertySection[] = orders.map((po) => ({
                title: po.productionOrderNumber,
                fields: [
                    { label: "Source", value: "D365" },
                    { label: "Status", value: po.productionOrderStatus },
                    { label: "Item Number", value: po.itemNumber },
                    { label: "Estimated Quantity", value: po.estimatedQuantity },
                    { label: "Delivery Date", value: po.deliveryDate || null },
                    { label: "Demand Sales Order", value: po.demandSalesOrderNumber },
                    { label: "Demand SO Line Lot", value: po.demandSalesOrderLineInventoryLotId || null },
                    { label: "Company", value: po.dataAreaId?.toUpperCase() ?? null },
                ],
            }));
            return { type: "properties", sections };
        }

        // Route nodes — route header + version comparison
        if (selectedNodeId.startsWith("Routes-")) {
            if (isD365Source && selectedNode) {
                return {
                    type: "properties",
                    sections: [{
                        title: `Route: ${selectedNode.data.routeId ?? selectedNodeId}`,
                        fields: [
                            { label: "Route ID", value: selectedNode.data.routeId ?? null },
                            { label: "Site ID", value: selectedNode.data.routeVersionSiteId ?? null },
                            { label: "Data Area", value: selectedNode.data.dataAreaId ?? null },
                            { label: "Source", value: "D365" },
                        ],
                    }],
                };
            }

            // Existing CS-side route comparison (current behavior)
            const rpKey = selectedNodeId.replace("Routes-", "");
            const routeComp = routeComparisonMap?.get(rpKey);
            if (routeComp?.isLoading) {
                return { type: "loading" };
            }
            return {
                type: "routeComparison",
                headerData: routeComp?.headerData,
                versionData: routeComp?.versionData,
            };
        }

        // Operation Detail nodes — CS property tables
        if (selectedNodeId.startsWith("OpDetail-")) {
            const node = nodes.find((n) => n.id === selectedNodeId);
            if (!node) {
                return { type: "empty" };
            }
            const sections: IPropertySection[] = [];

            // Section 1: Operation Properties
            const prop = node.data.operationProperty;
            if (prop) {
                sections.push({
                    title: `Operation ${splitPascalCase(prop.operationId)} (#${prop.operationNumber})`,
                    fields: [
                        ...(node.data.source === "d365" ? [{ label: "Source", value: "D365" }] : []),
                        { label: "Process Time", value: prop.processTime },
                        { label: "Setup Time", value: prop.setupTime },
                        { label: "Queue Time Before", value: prop.queueTimeBefore },
                        { label: "Queue Time After", value: prop.queueTimeAfter },
                        { label: "Transit Time", value: prop.transitTime },
                        { label: "Load Percentage", value: prop.loadPercentage },
                        { label: "Resource Quantity", value: prop.resourceQuantity },
                        { label: "Process Quantity", value: prop.processQuantity },
                        { label: "Route Group ID", value: prop.routeGroupId },
                        { label: "Route Type", value: prop.routeType },
                        { label: "Costing Resource ID", value: prop.costingOperationResourceId },
                        { label: "Process Cost Category", value: prop.processCostCategoryId },
                        { label: "Setup Cost Category", value: prop.setupCostCategoryId },
                        { label: "Quantity Cost Category", value: prop.quantityCostCategoryId },
                        { label: "Production Site ID", value: prop.productionSiteId },
                        { label: "Item Number", value: prop.itemNumber },
                    ],
                });
            }

            // Section 2: Resource Requirements
            const reqs = node.data.operationRequirements ?? [];
            if (reqs.length > 0) {
                for (const req of reqs) {
                    sections.push({
                        title: `Requirement: ${req.requiredCapabilityName || req.requiredOperationsResourceGroupId || req.requiredOperationsResourceId || "Resource"}`,
                        fields: [
                            { label: "Capability", value: req.requiredCapabilityName },
                            { label: "Resource Group", value: req.requiredOperationsResourceGroupId },
                            { label: "Resource", value: req.requiredOperationsResourceId },
                            { label: "Job Scheduling", value: req.willJobSchedulingUseRequirement },
                            { label: "Operation Scheduling", value: req.willOperationSchedulingUseRequirement },
                        ],
                    });
                }
            }

            if (sections.length === 0) {
                return { type: "loading" };
            }
            return { type: "properties", sections };
        }

        // BOM Lines nodes — summary table
        if (selectedNodeId.startsWith("BomLines-")) {
            const node = nodes.find((n) => n.id === selectedNodeId);
            if (!node?.data.bomLines || node.data.bomLines.length === 0) {
                return { type: "empty", message: "No BOM line data available." };
            }

            const sourcePrefix = node.data.source === "d365" ? [{ label: "Source", value: "D365" }] : [];
            const sections: IPropertySection[] = node.data.bomLines.map((bl) => ({
                title: `${bl.itemNumber} (${bl.dataAreaId})`,
                fields: [
                    ...sourcePrefix,
                    { label: "Quantity", value: bl.quantity },
                    { label: "Unit", value: bl.productUnitSymbol },
                    { label: "Line Type", value: bl.lineType },
                    { label: "Flushing Principle", value: bl.flushingPrinciple },
                    { label: "Warehouse", value: bl.consumptionWarehouseId },
                    { label: "Vendor Account", value: bl.vendorAccountNumber },
                    { label: "Operation #", value: bl.routeOperationNumber },
                    { label: "Std Sail Item", value: bl.stdSailItem },
                ],
            }));
            return { type: "properties", sections };
        }

        // FM Route nodes — site step properties
        if (selectedNodeId.startsWith("FMRoute-")) {
            const node = nodes.find((n) => n.id === selectedNodeId);
            if (!node) {
                return { type: "empty" };
            }
            if (node.data.status === "loading") {
                return { type: "loading" };
            }
            if (node.data.status === "error") {
                return { type: "error", message: "Could not load FM manufacturing route." };
            }
            const fmSteps = node.data.fmSiteSteps ?? [];
            if (fmSteps.length === 0) {
                return { type: "empty", message: "No FM site steps found." };
            }
            const sections: IPropertySection[] = fmSteps.map((step) => ({
                title: splitPascalCase(step.siteTypeName),
                fields: [
                    { label: "Site Name", value: step.siteName },
                    { label: "Division", value: step.divisionName },
                    { label: "Site ID", value: step.siteId },
                    { label: "Division ID", value: step.divisionId },
                    { label: "Country", value: step.countryCode },
                    { label: "Start Date", value: step.startDate },
                    { label: "Ship Date", value: step.shipDate },
                ],
            }));
            return { type: "properties", sections };
        }

        // Customer Payments — D365-only, one section per payment
        if (selectedEntityType === "CustomerPayments") {
            const node = nodes.find((n) => n.id === selectedNodeId);
            if (node?.data.status === "loading") {
                return { type: "loading" };
            }
            if (node?.data.status === "error") {
                return { type: "error", message: "Could not load customer payments from D365." };
            }
            const payments = node?.data.payments ?? [];
            if (payments.length === 0) {
                return { type: "empty", message: "No customer payments found in D365." };
            }
            const sections: IPropertySection[] = [];
            if (payments.length > 1 && node?.data.paymentsCurrency) {
                sections.push({
                    title: "Summary",
                    fields: [
                        { label: "Payments", value: payments.length },
                        { label: "Total", value: node.data.paymentsTotal ?? null },
                        { label: "Currency", value: node.data.paymentsCurrency ?? null },
                    ],
                });
            }
            for (const p of payments) {
                sections.push({
                    title: `Voucher ${p.voucher}`,
                    fields: [
                        { label: "Date", value: p.transDate },
                        { label: "Amount", value: p.amountCur },
                        { label: "Currency", value: p.currencyCode },
                        { label: "Customer Account", value: p.accountNum },
                        { label: "Company", value: p.dataAreaId },
                        { label: "Bank Account", value: p.companyBankAccountId },
                        { label: "Created By", value: p.createdBy1 },
                    ],
                });
            }
            return { type: "properties", sections };
        }

        // Vendor — D365-only properties view
        if (selectedNodeId.startsWith("Vendor-")) {
            const node = nodes.find((n) => n.id === selectedNodeId);
            const vendor = node?.data.vendor;
            if (!vendor) {
                return { type: "empty", message: "No vendor data available." };
            }
            const fields: IPropertySection["fields"] = [
                { label: "Account Number", value: vendor.vendorAccountNumber },
                { label: "Name", value: vendor.vendorName },
            ];
            if (vendor.vendorKnownAsName && vendor.vendorKnownAsName !== vendor.vendorName) {
                fields.push({ label: "Known As", value: vendor.vendorKnownAsName });
            }
            fields.push(
                { label: "Party Number", value: vendor.vendorPartyNumber },
                { label: "Vendor Group", value: vendor.vendorGroupId },
                { label: "City", value: vendor.addressCity },
                { label: "Country", value: vendor.addressCountryRegionId },
                { label: "Country (ISO)", value: vendor.addressCountryRegionISOCode },
                { label: "Recorded In", value: vendor.dataAreaId?.toUpperCase() ?? null },
                { label: "Intercompany", value: vendor.isIntercompany },
            );
            if (vendor.intercompanyDataAreaIds.length > 0) {
                fields.push({
                    label: "Manufactures In",
                    value: vendor.intercompanyDataAreaIds.map((d) => d.toUpperCase()).join(", "),
                });
            }
            return {
                type: "properties",
                sections: [{
                    title: vendor.vendorKnownAsName || vendor.vendorName || vendor.vendorAccountNumber,
                    fields,
                }],
            };
        }

        // BOM-sourced Released Products — conditional comparison vs properties
        if (selectedNodeId.startsWith("ReleasedProduct-")) {
            const rpKey = selectedNodeId.replace("ReleasedProduct-", "");

            if (selectedNode?.data.source === "d365") {
                const [itemNumber, dataAreaId] = rpKey.split("|");
                return {
                    type: "properties",
                    sections: [{
                        title: `Released Product: ${itemNumber}`,
                        fields: [
                            { label: "Item Number", value: itemNumber },
                            { label: "Data Area", value: dataAreaId },
                            { label: "Source", value: "D365 (deep BOM)" },
                        ],
                    }],
                };
            }

            // Check SO-line RP comparison first
            const soLineComparison = comparisonDataMap[selectedNodeId];
            if (soLineComparison) {
                return { type: "comparison", data: soLineComparison };
            }

            // Check BOM make-item comparison
            const bomComp = bomRPComparisonMap?.get(rpKey);
            if (bomComp) {
                if (bomComp.isLoading) {
                    return { type: "loading" };
                }
                if (bomComp.data) {
                    return { type: "comparison", data: bomComp.data };
                }
            }

            // Vendor/purchase BOM item — show context from BOM line data
            const [itemNumber, dataAreaId] = rpKey.split("|");
            let bomLineContext: { quantity: number; productUnitSymbol: string; lineType: string; vendorAccountNumber: string } | undefined;
            for (const [_key, bomLines] of (bomLineMap ?? [])) {
                const bl = bomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaID === dataAreaId);
                if (bl) {
                    bomLineContext = {
                        quantity: bl.quantity,
                        productUnitSymbol: bl.productUnitSymbol,
                        lineType: bl.lineType,
                        vendorAccountNumber: bl.vendorAccountNumber,
                    };
                    break;
                }
            }

            if (bomLineContext) {
                const node = nodes.find((n) => n.id === selectedNodeId);
                const d365Exists = node?.data.d365Exists;
                return {
                    type: "properties",
                    sections: [{
                        title: `Released Product: ${itemNumber}`,
                        fields: [
                            { label: "Item Number", value: itemNumber },
                            { label: "Data Area", value: dataAreaId },
                            { label: "D365 Exists", value: d365Exists === null ? "Loading..." : d365Exists === true ? "Yes" : d365Exists === false ? "No" : "Unknown" },
                            { label: "BOM Quantity", value: bomLineContext.quantity },
                            { label: "Unit", value: bomLineContext.productUnitSymbol },
                            { label: "Line Type", value: bomLineContext.lineType },
                            { label: "Vendor Account", value: bomLineContext.vendorAccountNumber },
                        ],
                    }],
                };
            }

            // Buy/stock item Released Product — show D365 data
            const buyRP = buyRPMap?.get(rpKey);
            if (buyRP) {
                if (buyRP.isLoading) {
                    return { type: "loading" };
                }
                if (buyRP.data) {
                    return {
                        type: "properties",
                        sections: [{
                            title: `Released Product: ${buyRP.data.itemNumber}`,
                            fields: [
                                { label: "Item Number", value: buyRP.data.itemNumber },
                                { label: "Product Number", value: buyRP.data.productNumber },
                                { label: "Company", value: buyRP.data.dataAreaID },
                                { label: "Default Order Type", value: buyRP.data.defaultOrderType },
                                { label: "Production Type", value: buyRP.data.productionType },
                                { label: "Item Model Group", value: buyRP.data.itemModelGroupId },
                                { label: "Product Group", value: buyRP.data.productGroupId },
                                { label: "Sales Price", value: buyRP.data.salesPrice },
                                { label: "Purchase Price", value: buyRP.data.purchasePrice },
                                { label: "Inventory Unit", value: buyRP.data.inventoryUnitSymbol },
                                { label: "Sales Unit", value: buyRP.data.salesUnitSymbol },
                                { label: "Purchase Unit", value: buyRP.data.purchaseUnitSymbol },
                                { label: "BOM Unit", value: buyRP.data.bomUnitSymbol },
                                { label: "Storage Dimension Group", value: buyRP.data.storageDimensionGroupName },
                                { label: "Tracking Dimension Group", value: buyRP.data.trackingDimensionGroupName },
                                { label: "Origin Country", value: buyRP.data.originCountryRegionId },
                                { label: "Primary Vendor", value: buyRP.data.primaryVendorAccountNumber },
                                { label: "Buyer Group", value: buyRP.data.buyerGroupId },
                            ],
                        }],
                    };
                }
                if (buyRP.isError) {
                    return { type: "error", message: "Could not load Released Product from D365." };
                }
            }

            return { type: "empty" };
        }

        // Standard comparison entities (SalesOrderHeader, Customer, VatNum, Charges, Product)
        const comparisonData = selectedNodeId
            ? comparisonDataMap[selectedNodeId]
            : undefined;
        if (comparisonData) {
            return { type: "comparison", data: comparisonData };
        }

        // Buy/stock Product — show D365 data as properties (no CS comparison)
        if (selectedNodeId.startsWith("Product-")) {
            const productNumber = selectedNodeId.replace("Product-", "");
            const buyProduct = buyProductMap?.get(productNumber);
            if (buyProduct) {
                if (buyProduct.isLoading) {
                    return { type: "loading" };
                }
                if (buyProduct.data) {
                    return {
                        type: "properties",
                        sections: [{
                            title: `Product: ${buyProduct.data.productNumber}`,
                            fields: [
                                { label: "Product Number", value: buyProduct.data.productNumber },
                                { label: "Product Name", value: buyProduct.data.productName },
                                { label: "Search Name", value: buyProduct.data.productSearchName },
                                { label: "Description", value: buyProduct.data.productDescription },
                                { label: "Product Type", value: buyProduct.data.productType },
                                { label: "Storage Dimension Group", value: buyProduct.data.storageDimensionGroupName },
                                { label: "Tracking Dimension Group", value: buyProduct.data.trackingDimensionGroupName },
                                { label: "Harmonized System Code", value: buyProduct.data.harmonizedSystemCode },
                            ],
                        }],
                    };
                }
                if (buyProduct.isError) {
                    return { type: "error", message: "Could not load Product from D365." };
                }
            }
        }

        return { type: "empty" };
    }, [
        selectedNodeId, selectedEntityType,
        isLineDetailSelected, isLinesListSelected,
        lineComparison.data, lineComparison.isLoading, lineComparison.error,
        existenceData, comparisonDataMap,
        routeComparisonMap, bomRPComparisonMap, bomLineMap,
        buyProductMap, buyRPMap,
        nodes,
    ]);
}
